import React, { useContext } from 'react';
import { RadioGroup, RadioButton } from '@cbrebuild/blocks';
import PropTypes from 'prop-types';
import moment from 'moment';

// import Tooltip from '../../nucleus/tooltip/tooltip';
import Datepicker from '../../nucleus/datepicker/datepicker';
import Select from '../../nucleus/select/select';

import './lease-abstraction.scss';
import PhoneInput from '../../nucleus/inputs/phone-input';
import PercentDollarInput from '../../nucleus/inputs/PercentDollarInput';
import LeaseAbstractionContext from './contexts/leaseAbstractionContext';
import { PARENT_KEYS, TRACKING_IDS } from '../../constants/lease-abstarction';

const LeaseAbstractionFieldType = Object.freeze({
  DATE: 0,
  TEXT: 1,
  TEXT_WITH_LABEL: 2,
  SELECT: 3,
  SELECT_WITH_LABEL: 4,
  RADIO: 5,
  PHONE: 6,
  PERCENT_DOLLAR: 7,
  READ_ONLY: 8,
});

const LeaseAbstractionField = ({
  fieldType,
  fieldData,
  fieldChangeHandler,
  fieldValidityChangeHandler,
  validateLeaseAbstractionFields,
  individualFieldChangeHandler,
  acceptFieldChangeHandler,
  asGroup,
  inputText,
  disabled,
  customPercentDollarField,
  showToolTip,
  percentFieldBaseTotal,
  percentFieldCustomIsPercent,
  percentFieldCustomOnTypeChange,
  percentageFieldCustomValue,
  percentageFieldCustomPercentage,
  percentDollarHideConversion,
  onBlur,
  onFieldFocus,
  isRequired,
}) => {
  const {
    findFieldRef,
    isMagnified,
    currentHighlightedField,
    isEnhancedFlow,
  } = useContext(LeaseAbstractionContext);

  // Required asterisks and the new error-styling class are only part of the new
  // enhanced split-pane flow. Gate both on isEnhancedFlow so the legacy
  // "Import Data to Voucher" modal (file-modal.js) keeps its original look.
  const showRequiredAsterisk = isEnhancedFlow && isRequired;
  const errorClass = (isEnhancedFlow && fieldData?.errorMessage?.length > 0) ? ' acc-field-has-error' : '';

  // Normalise two values for a loose diff comparison.
  // Numeric strings are compared as floats so "25.0" === "25" and "25.00" === "25".
  const valuesAreDifferent = (extracted, current) => {
    if (!extracted || extracted === '') return false;
    const a = String(extracted).trim().toLowerCase();
    const b = String(current).trim().toLowerCase();
    if (a === b) return false;
    // Skip numeric comparison for ISO date strings (YYYY-MM-DD) — parseFloat("2024-12-01")
    // returns 2024, causing dates in the same year to incorrectly compare as equal.
    const isDateLike = s => /^\d{4}-\d{2}-\d{2}/.test(s);
    if (!isDateLike(a) && !isDateLike(b)) {
      // Try numeric comparison to handle trailing-zero mismatches (e.g. "25.0" vs "25")
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA !== numB;
    }
    return true;
  };
  const handleFieldChange = (event) => {
    // Call the existing handler
    fieldChangeHandler(event);

    // Call the individual field change handler
    individualFieldChangeHandler(event);
  };

  const handleFieldBlur = (event) => {
    // Call the existing handler
    validateLeaseAbstractionFields(event);

    // Call the individual field change handler so the attachmentprops API fires
    // on blur (old flow: POST /mbb/attachmentprops/:id). In the new enhanced flow
    // individualModalFieldChangeHandler defaults to a no-op, so this is safe.
    individualFieldChangeHandler(event);
    onBlur(event);
  };

  const navigateToPageAndHighlightField = (key, pageIndex) => {
    const pageNumber = parseInt(pageIndex, 10);
    if (!Number.isNaN(pageNumber)) {
      findFieldRef(key, pageNumber);
    } else {
      console.error('Invalid page index');
    }
  };

  // Renders the extraction chip. When extractedValue differs from the current editable
  // value the chip shows the extracted value as purple underlined "click to accept" text.
  // The page-navigation button is always shown when a pageIndex is available.
  //
  // Legacy "Import Data to Voucher" modal flow (isEnhancedFlow === false) keeps the
  // original simple page indicator (<p className="acc-page-info">…Page: X</p>) instead
  // of the new green-dot accept chip. This preserves production UX for the old flow.
  const renderExtractionChip = (data, displayValue, chipLabel) => {
    if (!showToolTip || !data.toolTip?.pageIndex?.length) return null;
    if (!isEnhancedFlow) {
      return (
        <p className="acc-page-info">
          <div
            className="acc-page-number"
            onClick={() => navigateToPageAndHighlightField(fieldData.key, data.toolTip.pageIndex)}
          >
            Page: {data.toolTip.pageIndex}
          </div>
        </p>
      );
    }
    // chipDisplayValue on the data object takes priority — used when the rendered value
    // is transformed (e.g. free rent total months split into years/months display)
    const compareValue = data.chipDisplayValue !== undefined ? data.chipDisplayValue : (displayValue != null ? displayValue : data.value);
    const isDiff = valuesAreDifferent(data.extractedValue, compareValue);
    const label = chipLabel !== undefined ? chipLabel : data.extractedValue;
    const isLowConfidence = typeof data.toolTip?.confidence === 'number' && data.toolTip.confidence < 0.9;
    return (
      <div className={`acc-extraction-chip${isLowConfidence ? ' acc-extraction-chip--low' : ''}`}>
        <span className="acc-extraction-chip__icon" aria-hidden="true" />
        {isDiff ? (
          <button
            type="button"
            className="acc-extraction-chip__accept"
            title="Click to replace with extracted value"
            onMouseDown={(e) => {
              e.preventDefault(); // prevent input blur before value is set
              acceptFieldChangeHandler(data.extractedValue);
            }}
          >
            {label}
          </button>
        ) : (
          <span className="acc-extraction-chip__value">{label}</span>
        )}
        <button
          type="button"
          className="acc-extraction-chip__page"
          onClick={() => navigateToPageAndHighlightField(fieldData.key, data.toolTip.pageIndex)}
        >
          Page {data.toolTip.pageIndex}
        </button>
      </div>
    );
  };

  switch (fieldType) {
    case LeaseAbstractionFieldType.DATE:
      return (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${errorClass}`}>
            <label
              className={`acc-field-label ${
                isMagnified ? 'acc-field-label--magnified' : ''
              }`}
            >
              {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}<span>{fieldData.label}</span>
            </label>
            <Datepicker
              isHighlighted={
                currentHighlightedField &&
                currentHighlightedField ===
                  `${PARENT_KEYS[fieldData.key]}_${TRACKING_IDS[fieldData.key]}`
              }
              isMagnified={isMagnified}
              date={fieldData.data.value}
              onDateChange={handleFieldChange}
              updateValidity={fieldValidityChangeHandler}
              onFocus={() => {
                findFieldRef(
                  fieldData.key,
                  parseInt(fieldData.data.toolTip.pageIndex, 10),
                );
              }}
              onBlur={() => {
                findFieldRef('N/A', -1);
              }}
            />
            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
          {renderExtractionChip(
            fieldData.data,
            fieldData.data.value,
            fieldData.data.extractedValue && /^\d{4}-\d{2}-\d{2}/.test(fieldData.data.extractedValue)
              ? moment(fieldData.data.extractedValue).format('M/D/YYYY')
              : undefined,
          )}
        </div>
      );
    case LeaseAbstractionFieldType.RADIO:
      return (
        <div className="file-modal-group-container">
          <div className="acc-radio-group">
            <div className="acc-radio-group-label-container">
              <legend
                className={`acc-radio-group-label ${
                  isMagnified ? 'acc-radio-group-label--magnified' : ''
                }`}
              >
                {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label}
              </legend>
              {renderExtractionChip(
                fieldData.data,
                fieldData.radioButtons?.find(
                  rb => rb.value === (fieldData.data.value || '').toString().toLowerCase()
                )?.value,
                fieldData.radioButtons?.find(
                  rb => rb.value === (fieldData.data.extractedValue || '').toString().toLowerCase()
                )?.text,
              )}
            </div>
            <RadioGroup
              className="acc-radio-group-options"
              selectedValue={`${fieldData.data.value.toString().toLowerCase()}`}
              onChange={handleFieldChange}
              orientation="vertical"
            >
              {fieldData.radioButtons.map(({ value, text }) => (
                <RadioButton
                  la
                  key={text}
                  checked={fieldData.data.value.toString().toLowerCase() === value}
                  value={value}
                >
                  <p
                    className={`acc-radio-group-label ${
                      isMagnified ? 'acc-radio-group-label--magnified' : ''
                    }`}
                  >
                    {text}
                  </p>
                </RadioButton>
              ))}
            </RadioGroup>
          </div>
        </div>
      );
    case LeaseAbstractionFieldType.TEXT:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }${errorClass}`}
          >
            <label
              className={`${
                fieldData.label && fieldData.label.length > 0
                  ? 'acc-field-label'
                  : 'acc-field-label acc-field-label--transparent'
              } ${isMagnified ? 'acc-field-label--magnified' : ''}`}
            >
              {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label || 'Placeholder'}
            </label>
            <div className={`${inputText ? 'file-modal-input-with-text' : ''}`}>
              <input
                id={`${PARENT_KEYS[fieldData.key]}_${
                  TRACKING_IDS[fieldData.key]
                }`}
                disabled={disabled}
                type="text"
                className={`acc-input-text ${
                  isMagnified ? 'acc-input-text--magnified' : ''
                } ${
                  currentHighlightedField &&
                  currentHighlightedField ===
                    `${PARENT_KEYS[fieldData.key]}_${
                      TRACKING_IDS[fieldData.key]
                    }`
                    ? 'acc-input-text--highlighted'
                    : ''
                }`}
                value={fieldData.data.value}
                placeholder={inputText || fieldData.label}
                onChange={fieldChangeHandler}
                onBlur={(event) => {
                  handleFieldBlur(event);
                  findFieldRef('N/A', -1);
                }}
                onFocus={() => {
                  findFieldRef(
                    fieldData.key,
                    parseInt(fieldData.data.toolTip.pageIndex, 10),
                  );
                  onFieldFocus();
                }}
              />
              <p className="file-modal-input-text">
                <span>{inputText}</span>
              </p>
            </div>

            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
          {renderExtractionChip(fieldData.data, fieldData.data.value)}
        </div>
      );
    case LeaseAbstractionFieldType.TEXT_WITH_LABEL:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }${errorClass}`}
          >
            <label
              className={`${
                fieldData.label && fieldData.label.length > 0
                  ? 'acc-field-label'
                  : 'acc-field-label acc-field-label--transparent'
              } ${isMagnified ? 'acc-field-label--magnified' : ''}`}
            >
              {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label || 'Placeholder'}
              {renderExtractionChip(fieldData.data, fieldData.data.value)}
            </label>
            <div className={`${inputText ? 'file-modal-input-with-text' : ''}`}>
              <input
                disabled={disabled}
                type="text"
                className={`acc-input-text ${
                  isMagnified ? 'acc-input-text--magnified' : ''
                } ${
                  currentHighlightedField &&
                  currentHighlightedField ===
                    `${PARENT_KEYS[fieldData.key]}_${
                      TRACKING_IDS[fieldData.key]
                    }`
                    ? 'acc-input-text--highlighted'
                    : ''
                }`}
                value={fieldData.data.value}
                placeholder={inputText || fieldData.label}
                onChange={fieldChangeHandler}
                onBlur={(event) => {
                  handleFieldBlur(event);
                  findFieldRef('N/A', -1);
                }}
                onFocus={() => {
                  findFieldRef(
                    fieldData.key,
                    parseInt(fieldData.data.toolTip.pageIndex, 10),
                  );
                }}
              />
              <p className="file-modal-input-text">
                <span>{inputText}</span>
              </p>
            </div>

            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
        </div>
      );
    case LeaseAbstractionFieldType.SELECT:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }${errorClass}`}
          >
            <label
              className={`acc-field-label ${
                isMagnified ? 'acc-field-label--magnified' : ''
              }`}
            >
              {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label}
            </label>
            <Select
              className={fieldData?.errorMessage?.length > 0 ? 'acc-select-error' : ''}
              isMagnified={isMagnified}
              isHighlighted={
                currentHighlightedField &&
                currentHighlightedField ===
                  `${PARENT_KEYS[fieldData.key]}_${TRACKING_IDS[fieldData.key]}`
              }
              defaultOption={
                fieldData.data.value?.length > 0
                  ? (fieldData.options?.find(o => o.value === fieldData.data.value) || fieldData.data.value)
                  : 'Select'
              }
              showDefault={fieldData.data.value?.length === 0}
              options={fieldData.options}
              onChange={handleFieldChange}
              onClear={() => {}}
              disabled={disabled}
            />
            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
          {renderExtractionChip(fieldData.data, (() => { const opt = fieldData.options?.find(o => o.value === fieldData.data.extractedValue || o.display_name === fieldData.data.extractedValue); return (opt && opt.value === fieldData.data.value) ? fieldData.data.extractedValue : fieldData.data.value; })(), fieldData.options?.find(o => o.value === fieldData.data.extractedValue)?.display_name)}
        </div>
      );
    case LeaseAbstractionFieldType.SELECT_WITH_LABEL:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }${errorClass}`}
          >
            <label
              className={`acc-field-label ${
                isMagnified ? 'acc-field-label--magnified' : ''
              }`}
            >
              {showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label}
              {renderExtractionChip(fieldData.data, (() => { const opt = fieldData.options?.find(o => o.value === fieldData.data.extractedValue || o.display_name === fieldData.data.extractedValue); return (opt && opt.value === fieldData.data.value) ? fieldData.data.extractedValue : fieldData.data.value; })(), fieldData.options?.find(o => o.value === fieldData.data.extractedValue)?.display_name)}
            </label>
            <Select
              className={fieldData?.errorMessage?.length > 0 ? 'acc-select-error' : ''}
              isMagnified={isMagnified}
              isHighlighted={
                currentHighlightedField &&
                currentHighlightedField ===
                  `${PARENT_KEYS[fieldData.key]}_${TRACKING_IDS[fieldData.key]}`
              }
              defaultOption={
                fieldData.data.value?.length > 0
                  ? (fieldData.options?.find(o => o.value === fieldData.data.value) || fieldData.data.value)
                  : 'Select'
              }
              showDefault={fieldData.data.value?.length === 0}
              options={fieldData.options}
              onChange={handleFieldChange}
              onClear={() => {}}
            />
            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
        </div>
      );
    case LeaseAbstractionFieldType.PHONE:
      return (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${errorClass}`}>
            <label className="acc-field-label">{showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label}</label>
            <PhoneInput
              value={fieldData.data.value}
              onChange={fieldChangeHandler}
              onBlur={(value, isValid) => {
                fieldChangeHandler(value, isValid);
                individualFieldChangeHandler(value);
                findFieldRef('N/A', -1);
              }}
              hideLabel
              onFocus={() => {
                findFieldRef(
                  fieldData.key,
                  parseInt(fieldData.data.toolTip.pageIndex, 10),
                );
              }}
            />
            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
          {renderExtractionChip(fieldData.data, fieldData.data.value)}
        </div>
      );
    case LeaseAbstractionFieldType.PERCENT_DOLLAR:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }${errorClass}`}
          >
            <label className="acc-field-label">{showRequiredAsterisk && <span className="acc-required-asterisk">* </span>}{fieldData.label}</label>
            <PercentDollarInput
              value={
                percentageFieldCustomValue !== undefined
                  ? percentageFieldCustomValue
                  : parseFloat(fieldData.data.value[
                        `${customPercentDollarField?.value || 'dollars'}`
                      ])
              }
              hideConversion={percentDollarHideConversion}
              disabled={disabled}
              name={
                customPercentDollarField || {
                  percentage: 'percentage',
                  value: 'dollars',
                  select: 'isPercent',
                }
              }
              onChange={(e) => {
                fieldChangeHandler({
                  value: {
                    ...fieldData.data.value,
                    [e.target.name[0]]: e.target.value[e.target.name[0]],
                    [e.target.name[1]]: e.target.value[e.target.name[1]],
                  },
                });
              }}
              onBlur={individualFieldChangeHandler}
              onTypeChange={
                percentFieldCustomOnTypeChange !== undefined
                  ? (e) => {
                      percentFieldCustomOnTypeChange(e);
                    }
                  : (e) => {
                      fieldChangeHandler({
                        value: {
                          ...fieldData.data.value,
                          [e.target.name]: e.target.value,
                        },
                      });
                    }
              }
              baseTotal={parseFloat(percentFieldBaseTotal)}
              percentage={
                percentageFieldCustomPercentage !== undefined
                  ? percentageFieldCustomPercentage
                  : parseFloat(fieldData.data.value[
                        `${
                          customPercentDollarField?.percentage || 'percentage'
                        }`
                      ])
              }
              isPercent={
                percentFieldCustomIsPercent !== undefined
                  ? percentFieldCustomIsPercent
                  : fieldData.data.value[
                      `${customPercentDollarField?.select || 'isPercent'}`
                    ]
              }
              hideLabel
            />
            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData.errorMessage}</p>
            )}
          </div>
          {renderExtractionChip(fieldData.data, '')}
        </div>
      );
    case LeaseAbstractionFieldType.READ_ONLY:
      return (
        <div className="file-modal-group-container">
          <div
            className={`${
              asGroup ? 'file-modal-group-full' : 'file-modal-group'
            }`}
          >
            <label
              className={`${
                fieldData.label && fieldData.label.length > 0
                  ? 'acc-field-label'
                  : 'acc-field-label acc-field-label--transparent'
              }`}
            >
              {fieldData.label || 'Placeholder'}
              {renderExtractionChip(fieldData.data, fieldData.data.value)}
            </label>
            <div className={`${inputText ? 'file-modal-input-with-text' : ''}`}>
              <div className="acc-read-only">{fieldData.data.value}</div>
              <p className="file-modal-input-text">
                <span>{inputText}</span>
              </p>
            </div>

            {fieldData?.errorMessage?.length > 0 && (
              <p className="acc-error-message">{fieldData?.errorMessage}</p>
            )}
          </div>
        </div>
      );
    default:
      break;
  }
  return <div />;
};

LeaseAbstractionField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  fieldData: PropTypes.object,
  fieldType: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  fieldChangeHandler: PropTypes.func,
  individualFieldChangeHandler: PropTypes.func,
  fieldValidityChangeHandler: PropTypes.func,
  validateLeaseAbstractionFields: PropTypes.func,
  asGroup: PropTypes.bool,
  inputText: PropTypes.string,
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  customPercentDollarField: PropTypes.object,
  showToolTip: PropTypes.bool,
  percentFieldBaseTotal: PropTypes.number,
  percentFieldCustomIsPercent: PropTypes.bool,
  percentFieldCustomOnTypeChange: PropTypes.func,
  percentageFieldCustomValue: PropTypes.number,
  percentageFieldCustomPercentage: PropTypes.number,
  percentDollarHideConversion: PropTypes.bool,
  onBlur: PropTypes.func,
  acceptFieldChangeHandler: PropTypes.func,
  onFieldFocus: PropTypes.func,
  isRequired: PropTypes.bool,
};

LeaseAbstractionField.defaultProps = {
  fieldData: {},
  fieldType: 1,
  fieldChangeHandler: () => {},
  individualFieldChangeHandler: () => {},
  fieldValidityChangeHandler: () => {},
  validateLeaseAbstractionFields: () => {},
  asGroup: false,
  inputText: '',
  disabled: false,
  customPercentDollarField: {},
  showToolTip: true,
  percentFieldBaseTotal: 0,
  percentFieldCustomIsPercent: undefined,
  percentFieldCustomOnTypeChange: undefined,
  percentageFieldCustomValue: undefined,
  percentageFieldCustomPercentage: undefined,
  percentDollarHideConversion: false,
  onBlur: () => {},
  acceptFieldChangeHandler: () => {},
  onFieldFocus: () => {},
  isRequired: false,
};

export { LeaseAbstractionField, LeaseAbstractionFieldType };
